import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import FullPageMessage from 'src/view/components/FullPageMessage';
import FullPageLoader from 'src/view/components/FullPageLoader';
import { Home } from '@mui/icons-material';
import clientService from 'src/clientService';

interface EnvDetailsFetcherWrapperProps {
  children: React.ReactNode;
}

const EnvDetailsFetcherWrapper: FC<EnvDetailsFetcherWrapperProps> = ({ children }) => {
  const { id, siteId } = useParams<{ id: string; siteId: string }>();
  const { dispatch } = useAppState();
  const { showSnackbar, snackbarType } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {

    if (id) {
      setIsLoading(true);
      setError(null);

      clientService.api
        .readEnvironmentalProjectSummaryApiV1ProjectsProjectIdEnvironmentalSummaryGet(id)
        .then((response) => {
          const data = response.data;
          dispatch({ type: 'SET_PROJECT_ENVIRONMENTAL_ANALYSIS', payload: data });
          reset();
        })
        .catch((apiError) => {
          console.error('Failed to fetch environmental analysis details:', apiError);
          const errorMessage =
            apiError.response?.data?.detail ||
            `Failed to load carbon analysis details for ID: ${id}.`;
          setError(errorMessage);
          // showSnackbar({
          //   message: 'Error fetching environmental analysis details.',
          //   type: snackbarType.ERROR,
          // });
          reset();
        });

      clientService.api
        .readEnvironmentalAllSitesForProjectApiV1ProjectsProjectIdEnvironmentalSitesGet(id)
        .then((response) => {
          const data = response.data;
          dispatch({ type: 'SET_SITE_ENVIRONMENTAL_ANALYSIS', payload: data });
          reset();
        })
        .catch((apiError) => {
          console.error('Error fetching environmental site analysis data:', error);
          const errorMessage =
            apiError.response?.data?.detail ||
            `Failed to load environmental site analysis details for ID: ${id}.`;
          setError(errorMessage);
          showSnackbar({
            message: 'Error fetching environmental site analysis details.',
            type: snackbarType.ERROR,
          });
          reset();
        });
    } else {
      setError('Project ID is missing in the route parameters.');
      showSnackbar({ message: 'Project ID missing.', type: snackbarType.ERROR });
      reset();
    }
    return () => {
      reset();
    };
  }, [id, siteId]);

  const reset = () => {
    setIsLoading(false);
    setError(null);
  };


  if (isLoading) {
    return (
      <FullPageLoader
        title="Just a moment, crafting your project view..."
        message="Almost there..."
      />
    );
  }

  if (error) {
    return (
      <FullPageMessage
        headline="Uh-oh! The project data took an unexpected detour."
        subHeadline={`We're having a little trouble loading the details. The system reports: ${error}`}
        ctaText="Go to Home"
        onCtaClick={() => {
          window.location.href = '/';
        }}
        ctaIcon={<Home />}
      />
    );
  }

  return <>{children}</>;
};

export default EnvDetailsFetcherWrapper;
